import { inject, Injectable } from '@angular/core';
import { HttpGatewayService } from '../../core/services/http-gateway.service';
import { map, Observable } from 'rxjs';
import { AgentRequest, AgentResponse, AgentResponseContract } from '../types/agent-request';
import { UrlBuilder } from '../../shared/utils/url-utils';
import {
    AgentConversationOverview,
    AgentConversationOverviewContract,
    AgentResponseTurn,
    AgentResponseTurnContract,
} from '../types/agent-conversation';

const HOTEL_GPT_API_URLS = {
    QUERY: 'api/ai-platform/hotel-gpt/query',
    STATUS: 'api/ai-platform/hotel-gpt/status/{transactionId}',
    CONVERSATIONS: 'api/ai-platform/hotel-gpt/conversations',
    DELETE_CONVERSATION: 'api/ai-platform/hotel-gpt/conversations/{conversationId}/delete-conversation',
    CONVERSATION_TURNS: 'api/ai-platform/hotel-gpt/conversations/{conversationId}/turns',
    SET_RATING: 'api/ai-platform/hotel-gpt/conversations/{conversationId}/turns/{turnId}/set-rating',
};

@Injectable({ providedIn: 'root' })
export class HotelGptService {
    private http = inject(HttpGatewayService);

    public query(chainId: number, conversationId: string, question: string): Observable<AgentResponse> {
        const request: AgentRequest = {
            chain_id: chainId,
            conversation_id: conversationId,
            question,
        };

        return this.http.post<AgentResponseContract>(HOTEL_GPT_API_URLS.QUERY, request).pipe(
            map((contract) => ({
                transactionId: contract.transaction_id,
                turnId: contract.turn_id,
                status: contract.status,
                answer: contract.answer,
                options: contract.frontend_options,
                userRating: contract.user_rating,
            })),
        );
    }

    public getStatus(transactionId: string): Observable<AgentResponse> {
        const url = new UrlBuilder(HOTEL_GPT_API_URLS.STATUS).addTransactionId(transactionId).toUrl();

        return this.http.get<AgentResponseContract>(url).pipe(
            map((contract) => ({
                transactionId: contract.transaction_id,
                turnId: contract.turn_id,
                status: contract.status,
                answer: contract.answer,
                options: contract.frontend_options,
                userRating: contract.user_rating,
            })),
        );
    }

    public getConversations(chainId: number): Observable<AgentConversationOverview[]> {
        const url = new UrlBuilder(HOTEL_GPT_API_URLS.CONVERSATIONS)
            .addQueryParameter('chain_id', chainId.toString())
            .addQueryParameter('history_days', '30')
            .toUrl();
        return this.http.get<AgentConversationOverviewContract[]>(url).pipe(
            map((conversations) => {
                return conversations.map((conversation) => ({
                    conversationId: conversation.conversation_id,
                    question: conversation.question,
                    updatedAt: conversation.created_at,
                    hotels: conversation.hotels,
                })).sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
            }),
        );
    }

    public getConversationTurns(conversationId: string, chainId: number): Observable<AgentResponseTurn[]> {
        const url = new UrlBuilder(HOTEL_GPT_API_URLS.CONVERSATION_TURNS)
            .addConversationId(conversationId)
            .addQueryParameter('chain_id', chainId.toString())
            .addQueryParameter('turn_count', '20')
            .toUrl();
        return this.http.get<AgentResponseTurnContract[]>(url).pipe(
            map((contract) => {
                return contract.map((turn) => ({
                    question: turn.question,
                    response: turn.response,
                    options: turn.frontend_options,
                    hotels: turn.hotels,
                    createdAt: turn.created_at,
                    turnId: turn.turn_id,
                    userRating: turn.user_rating,
                })).reverse();
            })
        )
    }

    public setRating(conversationId: string, turnId: string, rating: -1 | 0 | 1): Observable<{status: string}> {
        const url = new UrlBuilder(HOTEL_GPT_API_URLS.SET_RATING)
            .addConversationId(conversationId)
            .addTurnId(turnId)
            .addQueryParameter('user_rating', rating.toString())
            .toUrl();

        return this.http.put(url, {});
    }

    public deleteConversation(conversationId: string): Observable<{status: string}> {
        const url = new UrlBuilder(HOTEL_GPT_API_URLS.DELETE_CONVERSATION)
            .addConversationId(conversationId)
            .toUrl();

        return this.http.put(url, {});
    }
}
